import React, { useCallback, useRef } from 'react'
import { useState } from 'react'

import { Undo2, X } from 'lucide-react'
import { Check } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'

import { UploadedFile } from 'openapi/models/UploadedFile'

import { cn } from 'utils/utils'

import { Button } from '@harvey-frontend/components/ui/button'
import { Spinner } from '@harvey-frontend/components/ui/spinner'
import Icon from 'components/ui/icon/icon'
import { Textarea } from 'components/ui/text-area'

import { ReferenceWithFile } from '../../../file-previewer/utils/helpers'
import { DraftingEditSuggestionExtended } from '../../stores/app-store'
import useAppStore from '../../stores/store'
import { OrderedCitations } from './OrderedCitations'

interface SuggestionProps {
  suggestion: DraftingEditSuggestionExtended
  referenceIndices: Record<string, number>
  idx: number
  isActivelyApplying: boolean
  referencesToFiles: Record<string, UploadedFile>
  onAccept: (idx: number) => void
  openDialog: (selectedReference: ReferenceWithFile | null) => void
}

export const Suggestion = ({
  suggestion,
  idx,
  isActivelyApplying,
  referenceIndices,
  referencesToFiles,
  onAccept,
  openDialog,
}: SuggestionProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const {
    suggestions,
    isFetching,
    isApplyingSuggestion,
    acceptedSuggestions,
    setSuggestions,
    dismissSuggestion,
    clearAcceptedSuggestions,
  } = useAppStore(
    useShallow((s) => ({
      suggestions: s.suggestions,
      isFetching: s.isFetching,
      isApplyingSuggestion: s.isApplyingSuggestion,
      acceptedSuggestions: s.acceptedSuggestions,
      setSuggestions: s.setSuggestions,
      dismissSuggestion: s.dismissSuggestion,
      clearAcceptedSuggestions: s.clearAcceptedSuggestions,
    }))
  )

  const handleUndo = async () => {
    if (suggestion.originalText) {
      const range = suggestion.range
      range.context.document.changeTrackingMode = 'Off'
      range.insertText(suggestion.originalText, Word.InsertLocation.replace)
      await range.context.sync()
    }
    clearAcceptedSuggestions()
  }
  const handleClick = async () => {
    const range = suggestion.range
    range.select()
    await range.context.sync()
  }
  const [initialSuggestion] = useState<string>(suggestion.suggestion)
  const isAccepted = acceptedSuggestions.includes(idx)
  const areInteractionsDisabled =
    isApplyingSuggestion || isActivelyApplying || isAccepted

  const updateSuggestions = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isFetching) return
    const newSuggestions = [...suggestions]
    newSuggestions[idx] = {
      ...suggestion,
      dirty: true,
      suggestion: e.target.value,
    }
    setSuggestions(newSuggestions)
  }

  const onFocusHandler = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Prevents "double focus", where the selection area in the textarea is focused immediately
    // after clicking.
    setTimeout(() => {
      e.target.select()
    }, 1)
  }

  const handleAccept = useCallback(() => {
    onAccept(idx)
  }, [onAccept, idx])
  const handleDismiss = useCallback(() => {
    dismissSuggestion(idx)
  }, [dismissSuggestion, idx])

  const renderActionButtons = () => {
    if (isAccepted) {
      return (
        <>
          <Button
            variant="outline"
            disabled={areInteractionsDisabled}
            className={cn('size-6', {
              'opacity-50': areInteractionsDisabled,
            })}
            size="sm"
            onClick={handleAccept}
          >
            <Icon icon={Check} className="size-4" />
          </Button>
          <Button
            variant="outline"
            className="size-6"
            size="sm"
            onClick={handleUndo}
          >
            <Icon size="small" icon={Undo2} />
          </Button>
        </>
      )
    }

    if (isActivelyApplying) {
      return (
        <Button
          variant="ghost"
          disabled
          className="flex h-6 items-center opacity-50"
        >
          <Spinner size="xxs" />
          <span className="text-xs">Applying…</span>
        </Button>
      )
    }

    return (
      <>
        <Button
          variant="outline"
          disabled={areInteractionsDisabled}
          className={cn('size-6', {
            'opacity-50': areInteractionsDisabled,
          })}
          size="sm"
          onClick={handleAccept}
        >
          <Icon icon={Check} className="size-4" />
        </Button>
        <Button
          variant="outline"
          disabled={areInteractionsDisabled}
          className={cn('size-6', {
            'opacity-50': areInteractionsDisabled,
          })}
          size="sm"
          onClick={handleDismiss}
        >
          <Icon size="small" icon={X} />
        </Button>
      </>
    )
  }

  return (
    <Button
      variant="ghost"
      className="flex h-fit flex-row items-start gap-1.5 rounded border bg-primary p-2 transition hover:bg-transparent"
      onClick={handleClick}
    >
      <div className="flex flex-[3] flex-col gap-1">
        <Textarea
          ref={textareaRef}
          className={cn(
            'min-h-0 w-full rounded-none border-none px-1 py-1 text-xs placeholder:text-xs focus:bg-secondary focus-visible:ring-0 focus-visible:ring-offset-0',
            {
              'resize-none': !isFetching,
              'cursor-not-allowed opacity-50': areInteractionsDisabled,
            }
          )}
          isFluid
          placeholder={initialSuggestion}
          disabled={areInteractionsDisabled}
          value={suggestion.suggestion}
          onChange={updateSuggestions}
          onFocus={onFocusHandler}
        />
        {suggestion.dirty && (
          <span className="absolute bottom-1 right-1 text-xs italic text-muted opacity-50">
            (edited)
          </span>
        )}
        <div className="px-1">
          <OrderedCitations
            textWithReferences={{
              text: suggestion.suggestion,
              references: suggestion.references ?? [],
            }}
            referenceIndices={referenceIndices}
            referencesToFiles={referencesToFiles}
            openDialog={openDialog}
          />
        </div>
      </div>
      <div className="flex shrink-0 gap-1.5 py-1">{renderActionButtons()}</div>
    </Button>
  )
}
